import React, { useRef } from 'react';
import useScreenLoop from '../hooks/useScreenLoop';
import './ContextWeb.css';

const LOOP = {
  initial: false,
  final: true,
  steps: [
    [true, 4200],
    [false, 1900],
  ],
};

// Coordinates are in the 254 x 96 viewBox, which is the panel's own size at
// desktop width; `drift` is where each chip wanders while the web is loose.
const HUB = { x: 127, y: 51, width: 70, height: 26, label: 'Inferno' };

const NODES = {
  beatrice: { x: 127, y: 15, width: 58, label: 'Beatrice', drift: [0, -5] },
  dante: { x: 40, y: 26, width: 46, label: 'Dante', drift: [-7, -4] },
  virgil: { x: 48, y: 79, width: 48, label: 'Virgil', drift: [-6, 5] },
  tone: { x: 206, y: 26, width: 76, label: 'Tone: mythic', note: true, drift: [7, -4] },
  research: { x: 202, y: 79, width: 62, label: 'Research', note: true, drift: [6, 5] },
};

// The draft reaches every thread first, then the characters find each other.
const THREADS = [
  ['hub', 'dante'],
  ['hub', 'virgil'],
  ['hub', 'beatrice'],
  ['hub', 'tone'],
  ['hub', 'research'],
  ['dante', 'virgil'],
  ['dante', 'beatrice'],
];

const NODE_HEIGHT = 18;
const pointOf = (id) => (id === 'hub' ? HUB : NODES[id]);

// A story's people and notes drift loose, then thread themselves to the
// draft and to each other, on a slow loop while on screen. Decorative.
export default function ContextWeb({ className = '' }) {
  const rootRef = useRef(null);
  const linked = useScreenLoop(rootRef, LOOP);

  return (
    <div
      ref={rootRef}
      className={`context-web ${linked ? 'is-linked' : ''} ${className}`.trim()}
      aria-hidden="true"
    >
      <svg className="context-web-art" viewBox="0 0 254 96" focusable="false">
        {THREADS.map(([from, to], index) => {
          const a = pointOf(from);
          const b = pointOf(to);
          return (
            <path
              key={`${from}-${to}`}
              className="context-web-thread"
              d={`M${a.x} ${a.y} L${b.x} ${b.y}`}
              pathLength="1"
              style={{ '--delay': `${120 + index * 90}ms` }}
            />
          );
        })}

        {Object.entries(NODES).map(([id, node]) => (
          <g
            key={id}
            className={`context-web-node ${node.note ? 'context-web-node--note' : ''}`.trim()}
            style={{ '--dx': `${node.drift[0]}px`, '--dy': `${node.drift[1]}px` }}
          >
            <rect
              x={node.x - node.width / 2}
              y={node.y - NODE_HEIGHT / 2}
              width={node.width}
              height={NODE_HEIGHT}
              rx={NODE_HEIGHT / 2}
            />
            <text x={node.x} y={node.y}>
              {node.label}
            </text>
          </g>
        ))}

        <g className="context-web-hub">
          <rect
            className="context-web-hub-ring"
            x={HUB.x - HUB.width / 2}
            y={HUB.y - HUB.height / 2}
            width={HUB.width}
            height={HUB.height}
            rx={HUB.height / 2}
          />
          <rect
            x={HUB.x - HUB.width / 2}
            y={HUB.y - HUB.height / 2}
            width={HUB.width}
            height={HUB.height}
            rx={HUB.height / 2}
          />
          <text x={HUB.x} y={HUB.y}>
            {HUB.label}
          </text>
        </g>
      </svg>
    </div>
  );
}
